/**
 * NOTIFICATION-CENTER-UX-D1-R1 — deterministic Ukrainian date buckets.
 *
 * Activity/display timestamp:
 * - prefer valid `updated_at` (canonical feed uses -updated_at);
 * - fall back to valid `created_at`;
 * - invalid/missing values degrade deterministically.
 *
 * Flattening rendered groups must preserve exact API/Redux input order.
 */

export const NOTIFICATION_DATE_GROUP = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  EARLIER: "earlier",
};

export const NOTIFICATION_DATE_GROUP_LABELS = {
  [NOTIFICATION_DATE_GROUP.TODAY]: "Сьогодні",
  [NOTIFICATION_DATE_GROUP.YESTERDAY]: "Вчора",
  [NOTIFICATION_DATE_GROUP.EARLIER]: "Раніше",
};

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseValidDate(iso) {
  if (typeof iso !== "string" || !iso.trim()) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/**
 * Canonical notification activity/display timestamp ISO string.
 */
export function resolveNotificationActivityTimestamp(notification) {
  if (!notification || typeof notification !== "object") return null;
  if (parseValidDate(notification.updated_at)) return notification.updated_at;
  if (parseValidDate(notification.created_at)) return notification.created_at;
  return null;
}

export function resolveNotificationDateGroupKey(iso, now = new Date()) {
  const activity = parseValidDate(iso);
  if (!activity) return NOTIFICATION_DATE_GROUP.EARLIER;

  const todayStart = startOfLocalDay(now);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  if (activity >= todayStart) return NOTIFICATION_DATE_GROUP.TODAY;
  if (activity >= yesterdayStart) return NOTIFICATION_DATE_GROUP.YESTERDAY;
  return NOTIFICATION_DATE_GROUP.EARLIER;
}

export function resolveNotificationDateGroupKeyForItem(item, now = new Date()) {
  return resolveNotificationDateGroupKey(
    resolveNotificationActivityTimestamp(item),
    now,
  );
}

/**
 * Group notifications into Сьогодні / Вчора / Раніше using activity timestamp.
 * Empty groups are omitted. Flattening group.items must equal the input order.
 */
export function groupNotificationsByDate(items, now = new Date()) {
  const buckets = {
    [NOTIFICATION_DATE_GROUP.TODAY]: [],
    [NOTIFICATION_DATE_GROUP.YESTERDAY]: [],
    [NOTIFICATION_DATE_GROUP.EARLIER]: [],
  };

  const list = Array.isArray(items) ? items : [];
  for (const item of list) {
    const key = resolveNotificationDateGroupKeyForItem(item, now);
    buckets[key].push(item);
  }

  const order = [
    NOTIFICATION_DATE_GROUP.TODAY,
    NOTIFICATION_DATE_GROUP.YESTERDAY,
    NOTIFICATION_DATE_GROUP.EARLIER,
  ];

  return order
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({
      key,
      label: NOTIFICATION_DATE_GROUP_LABELS[key],
      items: buckets[key],
    }));
}

export function flattenNotificationDateGroups(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const flat = [];
  for (const group of list) {
    if (group && Array.isArray(group.items)) {
      for (const item of group.items) flat.push(item);
    }
  }
  return flat;
}
